import { useMutation } from '@tanstack/react-query';
import {
  Destination,
  DestinationMetadataApiClient,
  DestinationsStorageApiClient,
} from '../services/DestinationsApiClient';
import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { v4 as uuidv4 } from 'uuid';
import { Analytics } from '../services/AnalyticsService';
import { AudioCompositionId, VideoCompositionId } from '../types/obs';

export async function connectCustomOutput({
  authContextProvider,
  serviceEnvironment,
  videoCompositionId,
  audioCompositionId,
  values,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  videoCompositionId: VideoCompositionId;
  audioCompositionId: AudioCompositionId;
  values: { outputName: string; server: string; streamKey: string };
}) {
  const destinationsStorageApiClient = new DestinationsStorageApiClient({
    authContextProvider,
    serviceEnvironment,
  });

  const destinations = await destinationsStorageApiClient.getDestinations();
  const destination = destinations.find((destination) => destination.displayName === values.outputName);

  if (destination) {
    console.warn(
      'Destination with the same name exists, might be confusing for the user to have two. Ask whether they are sure they want to keep it.'
    );
  }

  const destinationId = uuidv4();

  const result = await destinationsStorageApiClient.storeDestination({
    destination: {
      id: destinationId,
      displayName: values.outputName,
      provider: 'custom',
      providerId: '',
      accessToken: '',
      expiresAt: new Date(2050, 1, 1).toISOString(),
      refreshToken: '',
      refreshExpiresAt: new Date(2050, 1, 1).toISOString(),
      outputs: [
        {
          id: `${destinationId}-custom-1`,
          videoCompositionId,
          audioCompositionId,
          name: values.outputName,
          streamingSettings: {
            serverUrl: values.server,
            streamKey: values.streamKey,
          },
          auxiliaryData: {
            destinationId,
            isPrimaryOutput: false,
          },
          isEnabled: true,
        },
      ],
    },
  });

  await Analytics.reportMultiStreamingEvent({
    name: 'streaming_output_enabled',
    fields: [
      ['platform', 'custom'],
      ['streaming_outputs_added', 1],
    ],
  });

  return result;
}

export async function enableCustomStreamDestination({
  authContextProvider,
  serviceEnvironment,
  destination,
  outputId,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  destination: Destination;
  outputId: string;
}) {
  const destinationsStorageApiClient = new DestinationsStorageApiClient({
    authContextProvider,
    serviceEnvironment,
  });

  const destinationMetadata = await destinationsStorageApiClient.getDestinationById(destination);

  if (!destinationMetadata) {
    throw new Error('Destination not found');
  }

  const destinationMetadataApiClient = new DestinationMetadataApiClient({
    authContextProvider,
    serviceEnvironment,
    destinationMetadata,
  });

  return destinationMetadataApiClient.updateDestination({
    outputs: destination.outputs.map((output) => {
      if (output.id !== outputId) return output;

      return {
        ...output,
        isEnabled: true,
      };
    }),
  });
}

export function useEnableCustomStreamDestinationMutation({
  authContextProvider,
  serviceEnvironment,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
}) {
  return useMutation({
    mutationFn: async ({ destination, outputId }: { destination: Destination; outputId: string }) => {
      return enableCustomStreamDestination({
        authContextProvider,
        serviceEnvironment,
        destination,
        outputId,
      });
    },
  });
}
export function useConnectCustomOutput({
  authContextProvider,
  serviceEnvironment,
  videoCompositionId,
  audioCompositionId,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  videoCompositionId: VideoCompositionId;
  audioCompositionId: AudioCompositionId;
}) {
  return useMutation({
    mutationFn: async (values: { outputName: string; server: string; streamKey: string }) => {
      return connectCustomOutput({
        authContextProvider,
        serviceEnvironment,
        videoCompositionId,
        audioCompositionId,
        values,
      });
    },
  });
}
